import { cartStoreActions } from './cart-store.actions';
import { cartComputed, type AvailabilityItem, type FastCartTotalsData } from './cart.store';
import { type Cart, type CartItemInterface, type Money } from '../types/front.graphql';
import { type ObservableBoolean } from '@legendapp/state';

/**
 * PUBLIC API FOR CART STATE ACCESS
 *
 * This is the ONLY way external code should access cart state.
 * Direct access to cartStore$ is FORBIDDEN and will be removed.
 *
 * ARCHITECTURE PRINCIPLES:
 * - Encapsulation: Internal state is hidden behind getters
 * - Single Responsibility: Each function has a clear purpose
 * - Controlled Access: State mutations only through actions
 * - Type Safety: All functions are properly typed
 */

// ============================================================================
// CART STATE GETTERS - READ-ONLY ACCESS
// ============================================================================

export const cartStoreStateAPI = {
    // Cart identification
    getCartId: (): string => cartStoreActions.getCartId(),

    // Cart items - immutable access
    getCartItems: (): ReadonlyArray<CartItemInterface> => cartStoreActions.getCartItems(),
    getItems: (): ReadonlyArray<CartItemInterface> => cartStoreActions.getCartItems(),

    // Cart totals
    getCartTotal: (): Money => cartStoreActions.getCartTotal(),

    getCartData: () => cartStoreActions.getCartData(),

    getCartPrices: () => cartStoreActions.getCartPrices(),

    getCurrency: () => cartStoreActions.getCurrency(),

    getDiscounts: () => cartStoreActions.getDiscounts(),

    // ============================================================================
    // OBSERVABLE GETTERS - FOR REACTIVE SUBSCRIPTIONS WITH useSelector
    // ============================================================================

    /**
     * Get cart items observable for reactive subscriptions
     * Usage: const items = useSelector(() => cartStoreAPI.state.getCartItemsObservable().get())
     */
    getCartItemsObservable: () => cartStoreActions.getCartItemsObservable(),

    /**
     * Get cart data observable for reactive subscriptions
     * Usage: const cartData = useSelector(() => cartStoreAPI.state.getCartDataObservable().get())
     */
    getCartDataObservable: () => cartStoreActions.getCartDataObservable(),

    /**
     * Get cart prices observable for reactive subscriptions
     * Usage: const prices = useSelector(() => cartStoreAPI.state.getCartPricesObservable().get())
     */
    getCartPricesObservable: () => cartStoreActions.getCartPricesObservable(),

    /**
     * Get currency observable for reactive subscriptions
     * Usage: const currency = useSelector(() => cartStoreAPI.state.getCurrencyObservable().get())
     */
    getCurrencyObservable: () => cartStoreActions.getCurrencyObservable(),

    /**
     * Get discounts observable for reactive subscriptions
     * Usage: const discounts = useSelector(() => cartStoreAPI.state.getDiscountsObservable().get())
     */
    getDiscountsObservable: () => cartStoreActions.getDiscountsObservable(),

    /**
     * Get cart ID observable for reactive subscriptions
     * Usage: const cartId = useSelector(() => cartStoreAPI.state.getCartIdObservable().get())
     */
    getCartIdObservable: () => cartStoreActions.getCartIdObservable(),

    getAvailableItemsObservable: () => cartStoreActions.getCartAvailableItemsObservable(),

    /**
     * Get fast cart totals observable for reactive subscriptions
     * Usage: const totals = useSelector(() => cartStoreAPI.state.getCartTotals()?.get())
     */
    getCartTotals: () => cartStoreActions.getFastCartTotalsObservable(),

    /**
     * Get fast cart totals loading observable for reactive subscriptions
     * Usage: const loading = useSelector(() => cartStoreAPI.state.getCartTotalsLoading()?.get())
     */
    getCartTotalsLoading: () => cartStoreActions.getFastCartTotalsLoadingObservable(),

    // Complete cart state - immutable snapshot
    getCartState: () => {
        const state = cartStoreActions.getCartState();
        return Object.freeze({
            ...state,
            cartItems: Object.freeze(state.items),
        });
    },

    setAvailableItems: (items: Record<string, AvailabilityItem>) => cartStoreActions.setCartAvailableItems(items),
} as const;

// ============================================================================
// LOADING STATE GETTERS - READ-ONLY ACCESS
// ============================================================================

export const cartStoreLoadingAPI = {
    // Individual loading states
    isCartActionsLoading: (): boolean => cartStoreActions.isCartActionsLoading(),
    isFetchingCart: (): boolean => cartStoreActions.isFetchingCart(),
    isFetchingCartObservable: (): ObservableBoolean<boolean> => cartStoreActions.isFetchingCartObservable(),
    isCartSetupLoading: (): boolean => cartStoreActions.isCartSetupLoading(),

    // Combined loading state
    isAnyLoading: (): boolean => cartStoreActions.isAnyLoading(),

    // Complete loading state - immutable snapshot
    getLoadingState: () => {
        const loadingState = cartStoreActions.getLoadingState();
        return Object.freeze(loadingState);
    },
} as const;

// ============================================================================
// COMPUTED VALUES - READ-ONLY ACCESS
// ============================================================================

export const cartStoreComputedAPI = {
    // Item counts
    getTotalItems: (): number => cartComputed.getTotalItems(),
    getTotalUniqueItems: (): number => cartComputed.getTotalUniqueItems(),

    // Cart value
    getTotalValue: (): Money => cartComputed.getTotalValue(),

    // Cart state checks
    isEmpty: (): boolean => cartComputed.isEmpty(),
    isAnyLoading: (): boolean => cartComputed.isAnyLoading(),
} as const;

// ============================================================================
// CART ACTIONS - CONTROLLED MUTATIONS
// ============================================================================

export const cartStoreMutationAPI = {
    // Loading state mutations
    setCartActionsLoading: (isLoading: boolean): void => cartStoreActions.setCartActionsLoading(isLoading),
    setFetchingCartLoading: (isLoading: boolean): void => cartStoreActions.setFetchingCartLoading(isLoading),
    setCartSetupLoading: (isLoading: boolean): void => cartStoreActions.setCartSetupLoading(isLoading),

    // Cart identification mutations
    setCartId: (id: string): void => cartStoreActions.setCartId(id),

    // Cart item mutations
    addItemWithQuantity: (item: CartItemInterface, quantity: number): void =>
        cartStoreActions.addItemWithQuantity(item, quantity),
    addItemsWithQuantity: (items: CartItemInterface[]): void => cartStoreActions.addItemsWithQuantity(items),
    removeItemOrQuantity: (id: CartItemInterface['id'], quantity?: number): void =>
        cartStoreActions.removeItemOrQuantity(id, quantity),
    removeItem: (id: CartItemInterface['id']): void => cartStoreActions.removeItem(id),
    updateItemWithQuantity: (id: CartItemInterface['id'], quantity: number): void =>
        cartStoreActions.updateItemWithQuantity(id, quantity),

    // Bulk operations
    setItems: (items: CartItemInterface[], id: string): void => cartStoreActions.setItems(items, id),
    resetCart: (): void => cartStoreActions.resetCart(),
    /** Full reset including provider lifecycle flags. Use ONLY for store code changes. */
    resetCartForStoreChange: (): void => cartStoreActions.resetCartForStoreChange(),

    // Utility mutations
    updateCartLanguage: (language: string): void => cartStoreActions.updateCartLanguage(language),

    clearItems: (): void => cartStoreActions.clearItems(),

    // Fast cart totals mutations
    setFastCartTotals: (totals: FastCartTotalsData | undefined): void =>
        cartStoreActions.setFastCartTotals(totals),
    clearFastCartTotals: (): void => cartStoreActions.clearFastCartTotals(),
    setFastCartTotalsLoading: (loading: boolean): void => cartStoreActions.setFastCartTotalsLoading(loading),

    /**
     * Update cart store from server response (after GraphQL mutations or sync operations)
     * This is the RECOMMENDED way to update cart after any server operation.
     *
     * @param serverCart - Complete cart data from server
     * @param options - Optional configuration (updateTimestamps, clearIfEmpty)
     */
    updateCartFromServerResponse: (
        serverCart: Cart | undefined | null,
        options?: {
            updateTimestamps?: boolean;
            clearIfEmpty?: boolean;
        }
    ): void => cartStoreActions.updateCartFromServerResponse(serverCart, options),
} as const;

// ============================================================================
// UNIFIED PUBLIC API - SINGLE IMPORT POINT
// ============================================================================

/**
 * UNIFIED CART API
 *
 * This is the ONLY interface external code should use for cart operations.
 *
 * USAGE EXAMPLES:
 * ```typescript
 * // READ OPERATIONS
 * const cartId = cartAPI.state.getCartId();
 * const items = cartAPI.state.getCartItems();
 * const isLoading = cartAPI.loading.isAnyLoading();
 * const isEmpty = cartAPI.computed.isEmpty();
 *
 * // WRITE OPERATIONS
 * cartAPI.actions.addItemWithQuantity(item, 2);
 * cartAPI.actions.setCartActionsLoading(true);
 * cartAPI.actions.resetCart();
 * ```
 */
export const cartStoreAPI = {
    // Read-only state access
    state: cartStoreStateAPI,
    loading: cartStoreLoadingAPI,
    computed: cartStoreComputedAPI,

    // Controlled mutations
    actions: cartStoreMutationAPI,
} as const;

// ============================================================================
// TYPE EXPORTS FOR EXTERNAL USE
// ============================================================================

export type CartStoreStateAPI = typeof cartStoreStateAPI;
export type CartStoreLoadingAPI = typeof cartStoreLoadingAPI;
export type CartStoreComputedAPI = typeof cartStoreComputedAPI;
export type CartStoreMutationAPI = typeof cartStoreMutationAPI;
export type CartStoreAPI = typeof cartStoreAPI;
